/**
 * Customer Dead Lead List Configuration
 */
import type { TFunction } from 'i18next'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import type { CustomerDeadLead } from '@/modules/Customer/types'
import dayjs from 'dayjs'
import { createInternalLinkFormatter } from '@/components/ui/data-table-customized'
import {
    renderEmployeeNames,
    renderIndustry,
    getCommonCustomerFilters,
    JOURNEY_FILTER_DEFAULTS,
} from './customer-list-shared-config'

export const getDeadLeadColumns = (
    t: TFunction
): DataTableColumn<CustomerDeadLead>[] => [
    {
        key: 'customer_name',
        title: 'ID',
        width: 300,
        pinned: 'left',
        cellClassName: 'truncate uppercase',
        formatter: createInternalLinkFormatter({
            pathKey: 'customerDetail',
            pathParams: (_, row) => ({ id: row?.customer_id?.toString() }),
            label: (_, row) =>
                [row?.customer_id, row?.company_name || row?.customer_name]
                    .filter(Boolean)
                    .join(' - ') || '-',
        }),
    },
    {
        key: 'customer_sale_followers',
        title: t('customer_sales_followers'),
        width: 300,
        render: (value) => renderEmployeeNames(value, t('un-sales-assigned')),
    },
    {
        key: 'customer_industry',
        title: t('customer_industry'),
        width: 200,
        sortable: true,
        render: (value) =>
            renderIndustry(value, t('customer_industry.unknown')),
    },

    {
        key: 'customer_service_interestings',
        title: t('customer_service_interestings'),
        width: 350,
        render: (value) => {
            const services = value?.join(', ')
            if (!services)
                return (
                    <span className="text-gray-400 italic">
                        {t('un-services-assigned')}
                    </span>
                )
            return (
                <span className="truncate block" title={services}>
                    {services}
                </span>
            )
        },
    },
    {
        key: 'customer_updated_at',
        title: t('time_and_reason'),
        width: 300,
        sortable: true,
        render: (value, row) => {
            const note = row.customer_note || '-'
            const content = `${dayjs(value).format('DD/MM/YYYY HH:mm')}: ${note}`
            return (
                <span className="flex items-center gap-2" title={content}>
                    {dayjs(value).format('MMM DD, HH:mm')}
                    <span className="text-muted-foreground">{note}</span>
                </span>
            )
        },
    },
]

// ============================================================================
// Filter Configuration
// ============================================================================

export const getDeadLeadFilterItems = getCommonCustomerFilters

export const DEAD_LEAD_FILTER_DEFAULTS = {
    ...JOURNEY_FILTER_DEFAULTS,
}

// ============================================================================
// Sort Configuration
// ============================================================================

export const DEAD_LEAD_SORT_CONFIG = {
    defaultSort: {
        sort_by: 'customer_updated_at',
        sort_order: 'desc' as const,
    },
    sortableFields: [
        'customer_name',
        'customer_industry',
        'customer_updated_at',
    ],
}
